import { supabase } from '@/lib/supabaseClient';

// Shared demo login. The account is ephemeral + isolated + purged after every
// demo, so a fixed credential the entry page can auto-sign-in with is fine.
// Kept in sync with supabase/functions/demo-control/index.ts.
export const DEMO_LOGIN_EMAIL = 'parcelinkltd@gmail.com';
export const DEMO_LOGIN_PASSWORD = 'ParcelinkDemo!2026';

async function extractFnError(error, fallback) {
  try {
    if (error?.context && typeof error.context.clone === 'function') {
      const j = await error.context.clone().json();
      if (j?.error || j?.message) return j.error || j.message;
    }
  } catch { /* ignore */ }
  return error?.message || fallback;
}

// Public read (works pre-auth) — powers the login page + entry gate.
export async function getDemoState() {
  const { data, error } = await supabase.rpc('get_demo_state');
  if (error) return { is_on: false };
  return data || { is_on: false };
}

// Admin only. Clears any prior demo, creates the demo login, clones the chosen
// subcontractor into an isolated mirror.
export async function startDemo(sourceSubId) {
  const { data, error } = await supabase.functions.invoke('demo-control', {
    body: { action: 'start', source_sub_id: sourceSubId },
  });
  if (error) throw new Error(await extractFnError(error, 'Failed to start demo.'));
  if (data?.error) throw new Error(data.error);
  return data;
}

// ── Multi-attendee QR presentation sessions ─────────────────────────────────

// Admin only. Turns demo mode on and creates a session with a short join code.
export async function startDemoSession(sourceSubId, title) {
  const { data, error } = await supabase.functions.invoke('demo-control', {
    body: { action: 'session_start', source_sub_id: sourceSubId, title },
  });
  if (error) throw new Error(await extractFnError(error, 'Failed to start session.'));
  if (data?.error) throw new Error(data.error);
  return data.session;
}

// Admin only. Moves every attendee's deck bar to the given scene.
export async function setDemoScene(sessionId, scene) {
  const { data, error } = await supabase.functions.invoke('demo-control', {
    body: { action: 'session_scene', session_id: sessionId, scene },
  });
  if (error) throw new Error(await extractFnError(error, 'Failed to set scene.'));
  if (data?.error) throw new Error(data.error);
  return data;
}

// Admin only. Locks/opens parts on every attendee device: access_mode 'script'
// (cumulative unlock follows the scenes) or 'free' (everything open), the
// parts the presenter opened early, and whether pricing shows on attendee
// screens. Pass only what changed.
export async function setDemoAccess(sessionId, { accessMode, openedParts, showPricing } = {}) {
  const body = { action: 'session_access', session_id: sessionId };
  if (accessMode !== undefined) body.access_mode = accessMode;
  if (openedParts !== undefined) body.opened_parts = openedParts;
  if (showPricing !== undefined) body.show_pricing = showPricing;
  const { data, error } = await supabase.functions.invoke('demo-control', { body });
  if (error) throw new Error(await extractFnError(error, 'Failed to update device access.'));
  if (data?.error) throw new Error(data.error);
  return data;
}


// Admin only. The MAGIC MOMENT: allocate a chosen attendee's demo identity to
// their demo crew for tomorrow — their phone pings live in front of the room.
export async function magicAllocate(demoSubId) {
  const { data, error } = await supabase.functions.invoke('demo-control', {
    body: { action: 'magic_allocate', demo_sub_id: demoSubId },
  });
  if (error) throw new Error(await extractFnError(error, 'Magic moment failed.'));
  if (data?.error) throw new Error(data.error);
  return data;
}

// Admin only. Row counts per part_key for a candidate source subcontractor,
// so the console can flag modules that would open empty.
export async function getSourceReadiness(sourceId) {
  if (!sourceId) return {};
  const { data, error } = await supabase.rpc('demo_source_readiness', { p_source_id: sourceId });
  if (error) return {};
  return data || {};
}

// Admin only. Ends the session and purges every attendee identity.
export async function endDemoSession(sessionId) {
  const { data, error } = await supabase.functions.invoke('demo-control', {
    body: { action: 'session_end', session_id: sessionId },
  });
  if (error) throw new Error(await extractFnError(error, 'Failed to end session.'));
  if (data?.error) throw new Error(data.error);
  return data;
}

// Public (pre-auth) — validates a scanned QR code and powers scene polling.
// A FAILED call is 'error', never 'not_found': not_found is the server saying
// the session is gone (authoritative — the deck bar ends the demo on it and
// wipes the device's session marker), while a 4G blip, venue wifi drop or the
// wake-from-lock tick racing the radio is just a call that didn't get through.
// Conflating them ended live demos mid-room (owner walkthrough, 29 Sep —
// guidance died at scene 6 on a 2-bar connection). Callers keep their last
// known state on 'error'.
export async function getDemoSession(code) {
  if (!code) return { status: 'not_found' };
  const { data, error } = await supabase.rpc('get_demo_session', { p_code: code });
  if (error) return { status: 'error' };
  return data || { status: 'not_found' };
}

// ── Demo-wide settings (demo_settings, migration 20261228) ──────────────────
// Admin-editable knobs; the first is the goodbye screen's thank-you template.
// Reads fail soft to null so a missing table/row never breaks a live room.

export async function getDemoSetting(key) {
  try {
    const { data, error } = await supabase
      .from('demo_settings').select('value').eq('key', key).maybeSingle();
    if (error) return null;
    return data?.value ?? null;
  } catch { return null; }
}

export async function setDemoSetting(key, value) {
  const { error } = await supabase
    .from('demo_settings')
    .upsert({ key, value, updated_at: new Date().toISOString() });
  if (error) throw new Error(error.message);
  return true;
}

// ── Opening slide copy (demo_settings) ──────────────────────────────────────
// The first thing the room sees after a session starts: the join QR wrapped
// in the deck's problem-first voice. Both lines are admin-edited on Product
// Demos (Opening screen card); {company} resolves from the session title.
export const OPENING_HEADLINE_DEFAULT = 'Running crews on WhatsApp, spreadsheets and luck?';
export const OPENING_SUBLINE_DEFAULT =
  "Scan the code — you'll be running the platform on your own phone in thirty seconds.";

export function renderOpeningLine(template, fallback, sessionTitle) {
  const base = (template || '').trim() || fallback;
  const company = (sessionTitle || '').trim() || 'your team';
  return base.replaceAll('{company}', company);
}

/** The goodbye message with {company} resolved from the session title. */
export function renderThankYou(template, sessionTitle) {
  const base = (template || '').trim()
    || 'Thank you for watching, {company} — everything you drove tonight was live, on a real crew mirror. Your operation could be running it next week.';
  const company = (sessionTitle || '').trim() || 'your team';
  return base.replaceAll('{company}', company);
}

// ── QR invitation marker ────────────────────────────────────────────────────
// The demo is for invited guests, not for crews. The login page used to offer
// "View the live demo" to EVERY subcontractor whenever a demo happened to be
// running, which made a presentation tool look like a staff feature. The offer
// now appears only on a device that actually scanned the projected QR.
//
// Deliberately a local marker, not a permission: it decides what the login
// page OFFERS, never what anyone may DO. Entry is still gated by the session
// code and the signed NDA in demo-join, so clearing or forging this changes
// nothing a guest could not already do by opening /demo directly.
const DEMO_QR_KEY = 'pl_demo_qr';
const DEMO_QR_TTL_MS = 12 * 60 * 60 * 1000; // one presentation day

/** Remember that this device arrived from a scanned QR carrying a live code. */
export function markDemoQrInvite(code) {
  if (!code) return;
  try {
    localStorage.setItem(DEMO_QR_KEY, JSON.stringify({ code, at: Date.now() }));
  } catch { /* private mode — the offer simply will not show */ }
}

/** True when this device scanned a demo QR within the last 12 hours. */
export function hasDemoQrInvite() {
  try {
    const raw = localStorage.getItem(DEMO_QR_KEY);
    if (!raw) return false;
    const { at } = JSON.parse(raw) || {};
    if (!at || Date.now() - at > DEMO_QR_TTL_MS) {
      localStorage.removeItem(DEMO_QR_KEY);
      return false;
    }
    return true;
  } catch {
    return false; // unreadable or malformed — treat as no invitation
  }
}

// Public (pre-auth) — resolves the newest non-ended session for codeless entry.
// Still used by the admin's "Audience access" link in DemoControlPanel, which
// is a link the host hands out deliberately. It is NOT reachable from the crew
// login page any more.
export async function getActiveDemoSession() {
  const { data, error } = await supabase.rpc('get_active_demo_session');
  if (error) return { status: 'error' }; // failed call ≠ no session (see getDemoSession)
  return data || { status: 'not_found' };
}

// Attendee join: signs the agreement server-side (IP + device evidence) and
// provisions a personal demo identity. Returns credentials to sign in with.
export async function joinDemoSession(payload) {
  const { data, error } = await supabase.functions.invoke('demo-join', { body: payload });
  if (error) throw new Error(await extractFnError(error, 'Could not join the demo.'));
  if (data?.error) throw new Error(data.error);
  return data;
}

// Admin only. Purges ALL demo data + removes the demo login. Reinstates normal.
export async function stopDemo() {
  const { data, error } = await supabase.functions.invoke('demo-control', {
    body: { action: 'stop' },
  });
  if (error) throw new Error(await extractFnError(error, 'Failed to stop demo.'));
  if (data?.error) throw new Error(data.error);
  return data;
}

// The MASTER VIEWER is the first attendee who scanned into the live session
// (attendee_no 1). Their demo crew is the one the admin allocation board
// surfaces during a presentation — every other attendee's demo crew stays
// hidden so the board isn't flooded with one clone per phone. Admin-only
// (RLS gates each table); returns null when no session is live.
export async function getMasterDemoCrewId() {
  try {
    const { data: sess } = await supabase
      .from('demo_sessions')
      .select('id')
      .neq('status', 'ended')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!sess) return null;
    // First attendee = lowest number. Ordered with a limit rather than
    // eq(attendee_no, 1): legacy sessions hold duplicate numbers from the
    // pre-20261315 join race, and maybeSingle() errors on a multi-row match —
    // which is exactly how the board moment went missing (14 Oct audit).
    const { data: sig } = await supabase
      .from('demo_nda_signatures')
      .select('demo_sub_id')
      .eq('session_id', sess.id)
      .order('attendee_no', { ascending: true })
      .order('signed_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (!sig?.demo_sub_id) return null;
    const { data: sub } = await supabase
      .from('subcontractors')
      .select('crew_id')
      .eq('id', sig.demo_sub_id)
      .maybeSingle();
    return sub?.crew_id || null;
  } catch {
    return null;
  }
}
